/**
 * Transform API StoredScan format to Guardrail Insights report format
 *
 * This module handles the conversion between the scanner API's data format
 * and the format expected by the Guardrail Insights dashboard.
 */

import { getStoredToken, handleAuthFailure } from '../lib/api';

// API StoredScan format (from guardrail-api-worker)
export interface StoredScan {
  scanId: string;
  target: string;
  branch?: string;
  // What is being scanned. Kept as string to accept any target source
  // (github-repo, npm, pypi, mcp, agent, etc.) without blocking scans.
  targetType: string;
  timestamp: string;
  scores: { aivss: number; cvss: number; aars: number; crs?: number };
  findings: Finding[];
  fixes: FixRecommendation[];
  metadata: {
    name?: string;
    version?: string;
    author?: string;
    source?: string;
    filesScanned?: number;
    linesScanned?: number;
    duration?: number;
    githubTokenPresent?: boolean;
  };
  recommendation: 'SAFE' | 'CAUTION' | 'DO_NOT_INSTALL' | 'CONDITIONAL';
  // New fields from API
  vendorGuard?: VendorGuardData;
  creatorTrust?: {
    score: number;
    level: string;
    author: string;
    verified: boolean;
  };
  aivssBreakdown?: ScoreBreakdownData;
  cvssBreakdown?: ScoreBreakdownData;
  aarsBreakdown?: ScoreBreakdownData;
  tools?: ScanTool[];
  permissions?: ScanPermission[];
  aiBom?: RawAIBOMData;
  // RFIA (Rules File Integrity Analysis) — present when rfiaEnabled was true
  rfiaFindings?: RFIAFindingData[];
  rfiaAggregate?: RFIAAggregateData;

  // VibeGuard Engine (VGE) — present on all scans when files were available
  vibeguardFindings?: VGEFindingData[];
}

/** RFIA finding as returned by the API */
export interface RFIAFindingData {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  description: string;
  category: string;
  location?: { file: string; line?: number };
  cwe?: string;
  remediation?: string;
  source?: 'rfia';
}

/** VGE finding as returned by the API */
export interface VGEFindingData {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  description: string;
  category: string;
  location?: { file: string; line?: number; snippet?: string };
  cwe?: string;
  remediation?: string;
  confidence?: 'high' | 'medium' | 'low';
  source?: 'vge';
}

/** RFIA aggregate summary as returned by the API */
export interface RFIAAggregateData {
  filesScanned: number;
  totalFindings: number;
  worstScore: string; // TrafficLight: 'GREEN' | 'AMBER' | 'RED'
  totalBytes: number;
  totalDurationMs: number;
  bySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
  };
}

/** Tool record from the scanner API */
export interface ScanTool {
  name: string;
  description?: string;
  riskLevel?: 'high' | 'medium' | 'low';
  origin?: string;
  capabilities?: string[];
}

/** Permission record from the scanner API */
export interface ScanPermission {
  capability: string;
  riskLevel?: 'critical' | 'high' | 'medium' | 'low';
  tools?: string[];
  icon?: string;
  riskJustification?: string;
}

/** Raw AI-BOM data as received from the scanner — may contain nested summary or flat fields */
export interface RawAIBOMData {
  totalComponents?: number;
  byType?: Record<string, number>;
  byProvider?: Record<string, number>;
  llmProviders?: string[];
  components?: AIBOMComponent[];
  dependencies?: AIBOMDependency[];
  compliance?: AIBOMData['compliance'];
  summary?: {
    totalComponents?: number;
    byType?: Record<string, number>;
    byProvider?: Record<string, number>;
  };
}

export interface VendorGuardData {
  identity?: {
    username: string;
    platforms_found: string[];
    platform_count: number;
    cross_platform_consistency: string;
    gravatar_exists: boolean;
    identity_vintage_years: number;
    openpgp_key_found: boolean;
    openpgp_key_id: string | null;
  };
  breach_exposure?: {
    breach_count: number;
    oldest_breach_year: number | null;
    vintage_confirmed: boolean;
    is_ghost_profile: boolean;
  };
  security_hygiene?: {
    openssf_score: number | null;
    binary_artifacts_clean: boolean;
    branch_protection: boolean;
    pinned_dependencies: boolean;
    signed_commits: boolean;
    vulnerabilities_in_deps: number;
  };
  code_forensics?: {
    style_consistency: number;
    account_takeover_risk: boolean;
    malicious_patterns: string[];
    high_entropy_strings: boolean;
    hardcoded_ips: boolean;
    dangerous_calls: string[];
  };
  behavioral?: {
    timezone_consistent: boolean;
    claimed_location: string;
    inferred_timezone: string;
    is_abandonware: boolean;
    last_commit_days: number;
    open_critical_issues: number;
    bus_factor: number;
  };
  affiliation?: {
    corporate_domain_match: boolean;
    linkedin_verified: boolean;
    bio_entropy: string;
    employer_verified: string;
  };
  country_of_origin?: {
    inferred_country: string;
    country_code: string;
    confidence: string;
    signals: string[];
    timezone_country: string | null;
    location_country: string | null;
    email_domain_country: string | null;
    company_hq_country: string | null;
  };
  module_scores?: Record<
    string,
    { score: number; weight: number; status: string; summary: string }
  >;
  github_token_present?: boolean;
  /** camelCase variant — scanner may return either form */
  githubTokenPresent?: boolean;
  trust_score: number;
  /** camelCase variant — scanner may return either form */
  trustScore?: number;
  /** Raw module scores from the scanner (e.g. { identity: { score, weight, status, summary } }) */
  modules?: Record<string, { score: number; weight?: number; status?: string; summary?: string }>;
  risk_level: string;
  risk_factors: string[];
  trust_boosters: string[];
  summary: string;
}

export interface ScoreBreakdownData {
  [key: string]: {
    score: number;
    weight: number;
  };
}

export interface Finding {
  id: string;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  description: string;
  file?: string;
  line?: number;
  snippet?: string;
  fix?: string;
  category: string;
  /** Source engine that produced this finding (e.g. 'sae', 'dae', 'aie', 'gce', 'rfia', 'vge') */
  source?: 'sae' | 'dae' | 'aie' | 'gce' | 'zde' | 'rfia' | 'vge';
}

export interface FixRecommendation {
  id: string;
  findingId: string;
  severity: string;
  title: string;
  description: string;
  steps: string[];
  effort: 'low' | 'medium' | 'high';
  priority: number;
}

// Guardrail Insights format
export interface InsightsReport {
  reportId: string;
  reportDate: string;
  pluginName: string;
  branch?: string;
  classification: string;
  generatedAt: Date;
  scores: {
    aivss: { score: number; level: 'critical' | 'high' | 'medium' | 'low' };
    cvss: { score: number; level: 'critical' | 'high' | 'medium' | 'low' };
    aars: { score: number; level: 'critical' | 'high' | 'medium' | 'low' };
    creatorTrust?: { score: number; level: 'critical' | 'high' | 'medium' | 'low' };
    overall: number; // 0-100 composite risk score
    crs?: { score: number; level: 'critical' | 'high' | 'medium' | 'low' };
  };
  scoreBreakdowns: {
    aivss: ScoreBreakdownItem[];
    cvss: ScoreBreakdownItem[];
    aars: ScoreBreakdownItem[];
  };
  mcpTools: {
    totalTools: number;
    highRiskTools: number;
    mediumRiskTools: number;
    lowRiskTools: number;
    toolsList: string[];
    toolsInfo: Record<string, ToolInfo>;
  };
  vulnerabilities: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
  };
  keyFindings: KeyFinding[];
  creator: {
    name: string;
    organization: string;
    verified: boolean;
    country: string;
    organizationType: string;
    trustScore: number;
    repoUrl?: string;
    vendorGuard?: VendorGuardData;
  };
  plugin: {
    name: string;
    description: string;
    version: string;
    location: string;
    category: string;
    license: string;
  };
  vulnerabilityDetails: VulnerabilityDetail[];
  permissions: Permission[];
  recommendation: {
    type: 'approved' | 'approved-monitoring' | 'conditional' | 'rejected' | string;
    summary: string;
    conditions: string[];
  };
  threatModeling: ThreatModeling;
  prioritizedRemediation: RemediationItem[];
  riskAcceptanceItems: RiskAcceptanceItem[];

  // AI-BOM data (passed through from scanner)
  aiBom?: AIBOMData;

  // RFIA (Rules File Integrity Analysis) data
  rfiaFindings?: RFIAFindingData[];
  rfiaAggregate?: RFIAAggregateData;

  // VibeGuard Engine (VGE) findings
  vibeguardFindings?: VGEFindingData[];

  // Whether a GitHub token was available during the scan
  githubTokenPresent?: boolean;
}

// AI-BOM data structure
export interface AIBOMData {
  totalComponents: number;
  byType: Record<string, number>;
  llmProviders: string[];
  components?: AIBOMComponent[];
  dependencies?: AIBOMDependency[];
  compliance?: {
    hasPromptInjectionRisks: boolean;
    hasDataLeakageRisks: boolean;
    hasUnmonitoredModels: boolean;
    requiresAuditTrail: boolean;
    euAiActRiskLevel?: 'minimal' | 'limited' | 'high' | 'unacceptable';
  };
}

export interface AIBOMComponent {
  id: string;
  type: string;
  name: string;
  version?: string;
  provider?: string;
  description?: string;
  location: {
    file: string;
    line?: number;
  };
  risks: Array<{
    category: string;
    severity: string;
    description: string;
  }>;
}

export interface AIBOMDependency {
  name: string;
  version: string;
  license: string;
  licenseType?: 'permissive' | 'copyleft' | 'proprietary' | 'public-domain';
  compatible?: boolean;
  vulnerabilities?: Array<{
    cveId: string;
    severity: string;
    description: string;
  }>;
}

export interface ScoreBreakdownItem {
  name: string;
  value: number;
  maxValue: number;
  weight: number;
  description: string;
}

interface ToolInfo {
  name: string;
  description: string;
  riskLevel: 'high' | 'medium' | 'low';
  origin: string;
  capabilities: string[];
}

export interface KeyFinding {
  finding: string;
  status: 'pass' | 'warning' | 'fail' | 'info';
  details: string;
  linkedSection: string;
}

export interface VulnerabilityDetail {
  id: string;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  description: string;
  impact: string;
  remediation: string;
  cwe?: string;
  file?: string;
  line?: number;
  snippet?: string;
  source?: 'sae' | 'dae' | 'aie' | 'gce' | 'zde' | 'rfia' | 'vge';
}

interface Permission {
  capability: string;
  riskLevel: 'critical' | 'high' | 'medium' | 'low';
  tools: string[];
  icon: 'code' | 'network' | 'file' | 'terminal' | 'database';
  riskJustification: string;
}

/** Categories that map to a permission capability group */
const CATEGORY_CAPABILITY: Record<
  string,
  { capability: string; icon: Permission['icon']; justification: string }
> = {
  tool_abuse: {
    capability: 'Tool Execution',
    icon: 'terminal',
    justification: 'Findings indicate tools may be invoked in unintended or abusive ways.',
  },
  autonomy_risk: {
    capability: 'Autonomous Actions',
    icon: 'terminal',
    justification: 'Component exercises autonomous decision-making with potential side effects.',
  },
  data_exfiltration: {
    capability: 'Data Exfiltration',
    icon: 'database',
    justification: 'Patterns detected that could facilitate unauthorized data extraction.',
  },
  data_exposure: {
    capability: 'Data Access',
    icon: 'database',
    justification: 'Sensitive data may be accessible to this component.',
  },
  prompt_injection: {
    capability: 'Prompt Injection Surface',
    icon: 'code',
    justification: 'Input paths exist that could be used to manipulate model behaviour.',
  },
  injection: {
    capability: 'Injection Vulnerability',
    icon: 'code',
    justification: 'Code paths susceptible to injection attacks were identified.',
  },
  configuration: {
    capability: 'Configuration Access',
    icon: 'file',
    justification: 'Component has access to configuration or secrets.',
  },
  authentication: {
    capability: 'Authentication Bypass',
    icon: 'file',
    justification: 'Authentication mechanisms may be circumvented.',
  },
  authorization: {
    capability: 'Authorization Scope',
    icon: 'file',
    justification: 'Authorization controls may be insufficient.',
  },
  supply_chain: {
    capability: 'External Network Access',
    icon: 'network',
    justification: 'Component communicates with or depends on external services.',
  },
  malicious_code: {
    capability: 'Malicious Code Risk',
    icon: 'network',
    justification: 'Patterns consistent with malicious or backdoor code were detected.',
  },
};

/**
 * Derive permission capability groups from findings.
 * Runs on all scan targets — if findings contain relevant categories the
 * data is surfaced; otherwise the component shows a clean empty state.
 */
function generatePermissions(findings: Finding[]): Permission[] {
  // Group findings by mapped capability
  const groups = new Map<
    string,
    { meta: (typeof CATEGORY_CAPABILITY)[string]; riskLevel: string; tools: Set<string> }
  >();

  const severityOrder = ['critical', 'high', 'medium', 'low', 'info'];

  for (const f of findings) {
    const cat = (f.category || '').toLowerCase();
    const meta = CATEGORY_CAPABILITY[cat];
    if (!meta) continue;
    const existing = groups.get(meta.capability);
    if (!existing) {
      groups.set(meta.capability, { meta, riskLevel: f.severity, tools: new Set([f.title]) });
    } else {
      // Escalate to worst severity seen
      if (severityOrder.indexOf(f.severity) < severityOrder.indexOf(existing.riskLevel)) {
        existing.riskLevel = f.severity;
      }
      existing.tools.add(f.title);
    }
  }

  return Array.from(groups.values()).map(({ meta, riskLevel, tools }) => ({
    capability: meta.capability,
    riskLevel: riskLevel as Permission['riskLevel'],
    tools: Array.from(tools).slice(0, 6),
    icon: meta.icon,
    riskJustification: meta.justification,
  }));
}

export interface ThreatModeling {
  threatActors: ThreatActor[];
  attackVectors: AttackVector[];
  assetsCritical?: string[];
}

interface ThreatActor {
  name: string;
  motivation: string;
  capability: 'high' | 'medium' | 'low';
  likelihood: 'high' | 'medium' | 'low';
}

interface AttackVector {
  name: string;
  description: string;
  riskLevel: 'critical' | 'high' | 'medium' | 'low';
  mitigations: string[];
}

type RemediationPriority = 'immediate' | 'short-term' | 'medium-term' | 'long-term';
type RemediationEffort = 'low' | 'medium' | 'high';

export interface RemediationItem {
  id: string;
  title: string;
  description: string;
  riskLevel: 'critical' | 'high' | 'medium' | 'low' | 'info';
  priority: RemediationPriority;
  effort: RemediationEffort;
  estimatedTime: string;
  relatedVulnerability?: string;
  steps: string[];
}

export interface RiskAcceptanceItem {
  id: string;
  risk: string;
  riskLevel: 'critical' | 'high' | 'medium' | 'low' | 'info';
  rationale: string;
  residualRisk?: 'critical' | 'high' | 'medium' | 'low' | 'info';
}

/**
 * Derive risk acceptance items from medium/low findings.
 * Critical and high findings must be remediated — not accepted.
 * Capped at MAX_RISK_ACCEPTANCE items to keep the matrix scannable.
 */
const MAX_RISK_ACCEPTANCE = 8;
const RESIDUAL_RISK: Record<string, RiskAcceptanceItem['riskLevel']> = {
  medium: 'low',
  low: 'info',
};

function generateRiskAcceptanceItems(findings: Finding[]): RiskAcceptanceItem[] {
  return findings
    .filter((f) => f.severity === 'medium' || f.severity === 'low')
    .filter((f) => !!f.title)
    .slice(0, MAX_RISK_ACCEPTANCE)
    .map((f, i) => ({
      id: f.id || `RAM-${String(i + 1).padStart(3, '0')}`,
      risk: f.title,
      riskLevel: f.severity as RiskAcceptanceItem['riskLevel'],
      rationale: f.fix
        ? `Risk accepted pending: ${f.fix}`
        : (f.description || 'Risk accepted — monitor for changes in exposure.').slice(0, 200),
      residualRisk: RESIDUAL_RISK[f.severity],
    }));
}

/**
 * Get risk level from numeric score
 */
function getScoreLevel(score: number): 'critical' | 'high' | 'medium' | 'low' {
  if (score >= 9.0) return 'critical';
  if (score >= 7.0) return 'high';
  if (score >= 4.0) return 'medium';
  return 'low';
}

/**
 * Get trust level from score (inverted - higher is better)
 */
function getTrustLevel(score: number): 'critical' | 'high' | 'medium' | 'low' {
  if (score >= 8.0) return 'low'; // High trust = low risk
  if (score >= 6.0) return 'medium';
  if (score >= 4.0) return 'high';
  return 'critical'; // Low trust = critical risk
}

/**
 * Get recommendation type from API recommendation string
 */
function getRecommendationType(
  rec: string,
  criticalCount: number = 0,
  highCount: number = 0
): 'approved' | 'approved-monitoring' | 'conditional' | 'rejected' {
  // Override if critical/high vulnerabilities exist
  if (criticalCount > 0) return 'rejected';
  if (highCount > 0) return 'conditional';
  switch (rec) {
    case 'SAFE':
      return 'approved';
    case 'CAUTION':
      return 'approved-monitoring';
    case 'CONDITIONAL':
      return 'conditional';
    case 'DO_NOT_INSTALL':
      return 'rejected';
    default:
      return 'conditional';
  }
}

/**
 * Format date for report display
 */
function formatReportDate(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Count vulnerabilities by severity
 */
function countVulnerabilities(findings: Finding[]) {
  return {
    critical: findings.filter((f) => f.severity === 'critical').length,
    high: findings.filter((f) => f.severity === 'high').length,
    medium: findings.filter((f) => f.severity === 'medium').length,
    low: findings.filter((f) => f.severity === 'low').length,
    info: findings.filter((f) => f.severity === 'info').length,
  };
}

/**
 * Generate key findings from scan data
 */
function generateKeyFindings(scan: StoredScan): KeyFinding[] {
  const findings: KeyFinding[] = [];

  // Check overall safety
  if (scan.recommendation === 'SAFE') {
    findings.push({
      finding: 'Overall Risk Assessment: Safe',
      status: 'pass',
      details: 'Plugin passed security checks with acceptable risk level',
      linkedSection: 'vulnerability-assessment',
    });
  } else if (scan.recommendation === 'DO_NOT_INSTALL') {
    findings.push({
      finding: 'Critical Security Issues Found',
      status: 'fail',
      details: 'Plugin has critical vulnerabilities that must be addressed',
      linkedSection: 'vulnerability-assessment',
    });
  }

  // Check for critical/high findings
  const findingsArray = scan.findings || [];
  const criticalCount = findingsArray.filter((f) => f.severity === 'critical').length;
  const highCount = findingsArray.filter((f) => f.severity === 'high').length;

  if (criticalCount > 0) {
    findings.push({
      finding: `${criticalCount} Critical Vulnerabilities`,
      status: 'fail',
      details: 'Immediate attention required for critical issues',
      linkedSection: 'vulnerability-assessment',
    });
  }

  if (highCount > 0) {
    findings.push({
      finding: `${highCount} High Risk Findings`,
      status: 'warning',
      details: 'High severity issues should be addressed before production use',
      linkedSection: 'vulnerability-assessment',
    });
  }

  // Score-based findings
  if (scan.scores.aivss < 4.0) {
    findings.push({
      finding: 'Low AI Vulnerability Score',
      status: 'pass',
      details: `AIVSS score of ${scan.scores.aivss.toFixed(1)} indicates good AI safety controls`,
      linkedSection: 'score-breakdown',
    });
  } else if (scan.scores.aivss >= 7.0) {
    findings.push({
      finding: 'Elevated AI Risk Score',
      status: 'warning',
      details: `AIVSS score of ${scan.scores.aivss.toFixed(1)} indicates significant AI-related risks`,
      linkedSection: 'score-breakdown',
    });
  }

  // Metadata-based findings
  if (scan.metadata?.author) {
    findings.push({
      finding: 'Creator Identified',
      status: 'info',
      details: `Created by ${scan.metadata.author}`,
      linkedSection: 'creator-verification',
    });
  }

  return findings.slice(0, 5); // Limit to 5 key findings
}

/**
 * Convert findings to vulnerability details format
 */
function convertToVulnerabilityDetails(
  findings: Finding[],
  fixes: FixRecommendation[]
): VulnerabilityDetail[] {
  return findings.map((f) => {
    const fix = fixes.find((fx) => fx.findingId === f.id);
    return {
      id: f.id,
      title: f.title,
      severity: f.severity,
      description: f.description,
      impact: f.category ? `Affects ${f.category} security controls` : 'May impact system security',
      remediation: fix ? fix.steps.join(' ') : f.fix || 'Review and address manually',
      cwe: undefined, // CWE mapping would need to be added to the API
      file: f.file,
      line: f.line,
      snippet: f.snippet,
      source: f.source,
    };
  });
}

/** Maps severity → remediation priority tier and time estimate */
const SEVERITY_REMEDIATION: Record<
  string,
  { priority: RemediationPriority; estimatedTime: string; effort: RemediationEffort }
> = {
  critical: { priority: 'immediate', estimatedTime: '24–48 hours', effort: 'high' },
  high: { priority: 'short-term', estimatedTime: '1–2 weeks', effort: 'high' },
  medium: { priority: 'medium-term', estimatedTime: '1 month', effort: 'medium' },
  low: { priority: 'long-term', estimatedTime: '3+ months', effort: 'low' },
};

/**
 * Generate prioritized remediation items.
 * Prefers structured fixes when provided; falls back to deriving items from
 * findings so the section always has useful content from real scan data.
 * Info findings are excluded — they require no action.
 */
function generateRemediationItems(
  findings: Finding[],
  fixes: FixRecommendation[]
): RemediationItem[] {
  // Structured fixes from the scanner take priority when present
  if (fixes.length > 0) {
    return fixes
      .sort((a, b) => a.priority - b.priority)
      .map((fix) => {
        const sev = (fix.severity || 'medium') as string;
        const meta = SEVERITY_REMEDIATION[sev] ?? SEVERITY_REMEDIATION.medium;
        return {
          id: fix.id,
          title: fix.title,
          description: fix.description,
          riskLevel: sev as RemediationItem['riskLevel'],
          priority: meta.priority,
          effort: fix.effort ?? meta.effort,
          estimatedTime: meta.estimatedTime,
          relatedVulnerability: fix.findingId,
          steps: fix.steps,
        };
      });
  }

  // Derive from findings when no structured fixes are available
  return findings
    .filter((f) => f.severity !== 'info' && !!f.title)
    .sort((a, b) => {
      const order = ['critical', 'high', 'medium', 'low'];
      return order.indexOf(a.severity) - order.indexOf(b.severity);
    })
    .map((f, i) => {
      const meta = SEVERITY_REMEDIATION[f.severity] ?? SEVERITY_REMEDIATION.medium;
      const rawSteps = f.fix
        ? f.fix
            .split(/(?<=[.!?])\s+/)
            .map((s) => s.trim())
            .filter(Boolean)
        : [`Review ${f.category || 'the'} finding and apply appropriate controls.`];
      return {
        id: f.id || `REM-${String(i + 1).padStart(3, '0')}`,
        title: f.title,
        description: (f.description || f.title).slice(0, 300),
        riskLevel: f.severity as RemediationItem['riskLevel'],
        priority: meta.priority,
        effort: meta.effort,
        estimatedTime: meta.estimatedTime,
        relatedVulnerability: f.id,
        steps: rawSteps,
      };
    });
}

/**
 * Generate score breakdowns - use API data if available, otherwise generate defaults
 */
function generateScoreBreakdowns(scan: StoredScan) {
  // Use API-provided breakdowns if available
  const apiAivss = scan.aivssBreakdown;
  const apiCvss = scan.cvssBreakdown;
  const apiAars = scan.aarsBreakdown;

  const scores = scan.scores;

  // cap(v) ensures synthetic multiplier values never exceed the 0-10 scale
  const cap = (v: number) => Math.min(10, Math.max(0, v));

  return {
    aivss: [
      {
        name: 'Exploitability',
        value: cap(apiAivss?.exploitability?.score ?? scores.aivss * 0.85),
        maxValue: 10,
        weight: apiAivss?.exploitability?.weight ?? 30,
        description: 'Attack surface: vector, complexity, privileges, user interaction',
      },
      {
        name: 'AI Risk',
        value: cap(apiAivss?.aiRisk?.score ?? scores.aivss * 0.9),
        maxValue: 10,
        weight: apiAivss?.aiRisk?.weight ?? 35,
        description: 'Autonomy level, data access, tool permissions, blast radius',
      },
      {
        name: 'Impact',
        value: cap(apiAivss?.impact?.score ?? scores.aivss * 0.95),
        maxValue: 10,
        weight: apiAivss?.impact?.weight ?? 35,
        description: 'Confidentiality, integrity, and availability impact',
      },
    ],
    cvss: [
      {
        name: 'Exploitability',
        value: cap(apiCvss?.exploitability?.score ?? scores.cvss * 0.9),
        maxValue: 10,
        weight: apiCvss?.exploitability?.weight ?? 44,
        description: 'Attack vector, complexity, privileges, and user interaction',
      },
      {
        name: 'Impact',
        value: cap(apiCvss?.impact?.score ?? scores.cvss * 0.85),
        maxValue: 10,
        weight: apiCvss?.impact?.weight ?? 56,
        description: 'Confidentiality, integrity, and availability impact',
      },
    ],
    // AARS breakdown uses actual 7-factor values from the scoring engine when
    // available, with sensible fallbacks for older scans that lack the breakdown.
    aars:
      scores.aars > 0
        ? [
            {
              name: 'Decision Authority',
              value: cap(apiAars?.decisionAuthority?.score ?? scores.aars * 0.9),
              maxValue: 10,
              weight: apiAars?.decisionAuthority?.weight ?? 20,
              description: 'Scope of autonomous decision-making (higher = riskier)',
            },
            {
              name: 'Human Oversight',
              value: cap(apiAars?.humanInLoop?.score ?? 10 - scores.aars * 0.7),
              maxValue: 10,
              weight: apiAars?.humanInLoop?.weight ?? 20,
              description: 'Human-in-the-loop controls (higher = safer)',
            },
            {
              name: 'Impact Scope',
              value: cap(apiAars?.impactScope?.score ?? scores.aars * 0.85),
              maxValue: 10,
              weight: apiAars?.impactScope?.weight ?? 15,
              description: 'Breadth of potential impact from agent actions (higher = riskier)',
            },
            {
              name: 'Reversibility',
              value: cap(apiAars?.reversibility?.score ?? 10 - scores.aars * 0.6),
              maxValue: 10,
              weight: apiAars?.reversibility?.weight ?? 15,
              description: 'Ability to undo agent actions (higher = safer)',
            },
            {
              name: 'Time Pressure',
              value: cap(apiAars?.timePressure?.score ?? scores.aars * 0.3),
              maxValue: 10,
              weight: apiAars?.timePressure?.weight ?? 15,
              description: 'Real-time or autonomous scheduling constraints (higher = riskier)',
            },
            {
              name: 'Transparency',
              value: cap(apiAars?.transparency?.score ?? 10 - scores.aars * 0.5),
              maxValue: 10,
              weight: apiAars?.transparency?.weight ?? 8,
              description: 'Logging and observability of agent behavior (higher = safer)',
            },
            {
              name: 'Error Handling',
              value: cap(apiAars?.errorHandling?.score ?? 10 - scores.aars * 0.5),
              maxValue: 10,
              weight: apiAars?.errorHandling?.weight ?? 7,
              description: 'Quality of error recovery mechanisms (higher = safer)',
            },
          ]
        : [],
  };
}

/**
 * Generate default threat modeling based on findings
 */
function generateThreatModeling(findings: Finding[]): ThreatModeling {
  const hasCritical = findings.some((f) => f.severity === 'critical');
  const hasHigh = findings.some((f) => f.severity === 'high');

  return {
    threatActors: [
      {
        name: 'Malicious User',
        motivation: 'Data exfiltration or system compromise',
        capability: hasHigh ? 'high' : 'medium',
        likelihood: hasCritical ? 'high' : 'medium',
      },
      {
        name: 'Compromised Dependency',
        motivation: 'Supply chain attack',
        capability: 'high',
        likelihood: 'low',
      },
      {
        name: 'Prompt Injection Attacker',
        motivation: 'Manipulate agent behavior',
        capability: 'medium',
        likelihood: hasHigh ? 'high' : 'medium',
      },
    ],
    attackVectors: findings.slice(0, 3).map((f) => ({
      name: f.title,
      description: f.description,
      riskLevel: f.severity === 'info' ? 'low' : f.severity,
      mitigations: f.fix ? [f.fix] : ['Review and implement appropriate controls'],
    })),
  };
}

/**
 * Extract creator information from scan data
 * Uses vendorGuard data if available, falls back to metadata
 */
function deriveRepoUrl(target: string): string | undefined {
  if (!target) return undefined;
  // Already a full GitHub URL
  if (/^https?:\/\/github\.com\/[^/]+\/[^/]+/.test(target)) {
    const match = target.match(/^(https?:\/\/github\.com\/[^/]+\/[^/]+)/);
    return match ? match[1].replace(/^http:/, 'https:') : undefined;
  }
  // owner/repo shorthand (no slashes beyond one, no spaces, no protocol)
  if (/^[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+$/.test(target)) {
    return `https://github.com/${target}`;
  }
  return undefined;
}

function extractCreatorInfo(scan: StoredScan): {
  name: string;
  organization: string;
  verified: boolean;
  country: string;
  organizationType: string;
  trustScore: number;
  repoUrl?: string;
  vendorGuard?: VendorGuardData;
} {
  const vg = scan.vendorGuard;
  const ct = scan.creatorTrust;

  // If we have vendorGuard data, use it.
  // Handle both snake_case (VendorGuardData GUI format) and camelCase (scanner VendorGuardResult format)
  if (vg) {
    // API may return trust_score (snake_case from full VendorGuard) or trustScore (camelCase from simplified API shape)
    const rawScore = vg.trust_score ?? vg.trustScore ?? 0;

    // Extract module scores from the raw modules object (scanner format: { identity: { score, weight, status, summary }, ... })
    const rawModules = vg.modules;
    let moduleScores: VendorGuardData['module_scores'];
    if (rawModules && typeof rawModules === 'object') {
      moduleScores = {};
      for (const [key, mod] of Object.entries(rawModules)) {
        const m = mod;
        if (m && typeof m.score === 'number') {
          // Weights from the scanner are decimals (0.2 = 20%); normalise to percentage
          const rawWeight = m.weight ?? 0;
          const weightPct = rawWeight > 0 && rawWeight <= 1 ? rawWeight * 100 : rawWeight;
          moduleScores[key] = {
            score: m.score,
            weight: weightPct,
            status: m.status ?? 'unknown',
            summary: m.summary ?? '',
          };
        }
      }
    }

    // Normalise camelCase → snake_case for github token flag
    const tokenPresent = vg.github_token_present ?? vg.githubTokenPresent;

    const vgWithScores: VendorGuardData = {
      ...vg,
      ...(moduleScores ? { module_scores: moduleScores } : {}),
      ...(tokenPresent !== undefined ? { github_token_present: tokenPresent } : {}),
    };

    return {
      name: vg.identity?.username || scan.metadata?.author || 'Unknown',
      organization: vg.affiliation?.employer_verified || 'Unknown',
      verified: ct?.verified ?? rawScore >= 70,
      country: vg.country_of_origin?.inferred_country || 'Unknown',
      organizationType: vg.affiliation?.corporate_domain_match ? 'Corporate' : 'Individual',
      trustScore: ct?.score ?? (rawScore > 10 ? rawScore / 10 : rawScore),
      repoUrl: deriveRepoUrl(scan.target),
      vendorGuard: vgWithScores,
    };
  }

  // If we have creatorTrust but no vendorGuard
  if (ct) {
    return {
      name: ct.author || scan.metadata?.author || 'Unknown',
      organization: 'Unknown',
      verified: ct.verified,
      country: 'Unknown',
      organizationType: 'Unknown',
      trustScore: ct.score,
      repoUrl: deriveRepoUrl(scan.target),
      vendorGuard: undefined,
    };
  }

  // Fallback to basic metadata
  return {
    name: scan.metadata?.author || 'Unknown',
    organization: 'Unknown',
    verified: !!scan.metadata?.author,
    country: 'Unknown',
    organizationType: 'Unknown',
    trustScore: scan.metadata?.author ? 7.5 : 5.0,
    repoUrl: deriveRepoUrl(scan.target),
    vendorGuard: undefined,
  };
}

/**
 * Main transformation function
 * Converts a StoredScan from the API to the InsightsReport format
 */
export function transformScanToInsights(scan: StoredScan): InsightsReport {
  const pluginName = scan.metadata?.name || scan.target.split('/').pop() || 'Unknown Plugin';
  const reportDate = formatReportDate(scan.timestamp);

  // Extract creator info from vendorGuard or fallback
  const creatorInfo = extractCreatorInfo(scan);

  return {
    reportId: scan.scanId,
    reportDate,
    pluginName,
    branch: scan.branch,
    classification: 'Confidential',
    generatedAt: new Date(scan.timestamp),

    scores: {
      aivss: { score: scan.scores.aivss, level: getScoreLevel(scan.scores.aivss) },
      cvss: { score: scan.scores.cvss, level: getScoreLevel(scan.scores.cvss) },
      aars: { score: scan.scores.aars, level: getScoreLevel(scan.scores.aars) },
      creatorTrust: { score: creatorInfo.trustScore, level: getTrustLevel(creatorInfo.trustScore) },
      overall: Math.min(100, Math.round(
        scan.scores.cvss * 10 * 0.4 + scan.scores.aivss * 10 * 0.3 + scan.scores.aars * 0.3
      )),
      ...(scan.scores.crs != null && {
        crs: {
          score: scan.scores.crs,
          level: scan.scores.crs >= 800 ? 'low' as const
            : scan.scores.crs >= 600 ? 'medium' as const
            : scan.scores.crs >= 400 ? 'high' as const
            : 'critical' as const,
        },
      }),
    },

    scoreBreakdowns: generateScoreBreakdowns(scan),

    mcpTools: {
      totalTools: scan.tools?.length || 0,
      highRiskTools: scan.tools?.filter((t) => t.riskLevel === 'high').length || 0,
      mediumRiskTools: scan.tools?.filter((t) => t.riskLevel === 'medium').length || 0,
      lowRiskTools: scan.tools?.filter((t) => t.riskLevel === 'low').length || 0,
      toolsList: scan.tools?.map((t) => t.name) || [],
      toolsInfo: {},
    },

    vulnerabilities: countVulnerabilities(scan.findings || []),

    keyFindings: generateKeyFindings(scan),

    creator: creatorInfo,

    plugin: {
      name: pluginName,
      description: `Security scan of ${scan.targetType}: ${scan.target}`,
      version: scan.metadata?.version || 'Unknown',
      location: scan.target,
      category: scan.targetType,
      license: 'Unknown',
    },

    vulnerabilityDetails: convertToVulnerabilityDetails(scan.findings || [], scan.fixes || []),

    permissions: generatePermissions(scan.findings || []),

    recommendation: (() => {
      const findingsArray = scan.findings || [];
      const criticalCount = findingsArray.filter((f) => f.severity === 'critical').length;
      const highCount = findingsArray.filter((f) => f.severity === 'high').length;
      return {
        type: getRecommendationType(scan.recommendation, criticalCount, highCount),
        summary: getRecommendationSummary(scan, criticalCount, highCount),
        conditions: getRecommendationConditions(scan),
      };
    })(),

    threatModeling: generateThreatModeling(scan.findings || []),

    prioritizedRemediation: generateRemediationItems(scan.findings || [], scan.fixes || []),

    riskAcceptanceItems: generateRiskAcceptanceItems(scan.findings || []),

    // Pass through RFIA data from scanner
    rfiaFindings: scan.rfiaFindings,
    rfiaAggregate: scan.rfiaAggregate,

    // Pass through VGE data from scanner
    vibeguardFindings: scan.vibeguardFindings,

    // Pass through AI-BOM data from scanner
    aiBom: scan.aiBom
      ? {
          totalComponents: scan.aiBom.summary?.totalComponents ?? scan.aiBom.totalComponents ?? 0,
          byType: scan.aiBom.summary?.byType ?? scan.aiBom.byType ?? {},
          // llmProviders may be a direct array (API format) or derived from byProvider map
          llmProviders:
            scan.aiBom.llmProviders ||
            Object.keys(scan.aiBom.summary?.byProvider || scan.aiBom.byProvider || {}),
          components: scan.aiBom.components,
          dependencies: scan.aiBom.dependencies,
          compliance: scan.aiBom.compliance,
        }
      : undefined,

    // GitHub token presence — check metadata, vendorGuard, or raw scan fields
    githubTokenPresent:
      scan.metadata?.githubTokenPresent ??
      scan.vendorGuard?.github_token_present ??
      scan.vendorGuard?.githubTokenPresent,
  };
}

function getRecommendationSummary(
  scan: StoredScan,
  criticalCount: number = 0,
  highCount: number = 0
): string {
  // Override based on actual vulnerabilities
  if (criticalCount > 0)
    return 'This component has critical security issues and should not be used until they are resolved.';
  if (highCount > 0)
    return 'This component has high-severity issues that require attention before production use.';
  switch (scan.recommendation) {
    case 'SAFE':
      return 'This component has passed security assessment with acceptable risk levels. Recommended for use.';
    case 'CAUTION':
      return 'This component has moderate security concerns. Can be used with monitoring and additional controls.';
    case 'CONDITIONAL':
      return 'This component requires specific conditions to be met before use. Review findings and implement required mitigations.';
    case 'DO_NOT_INSTALL':
      return 'This component has critical security issues and should not be used until they are resolved.';
    default:
      return 'Security assessment completed. Review findings for detailed recommendations.';
  }
}

function getRecommendationConditions(scan: StoredScan): string[] {
  const conditions: string[] = [];
  const findingsArray = scan.findings || [];

  if (findingsArray.some((f) => f.severity === 'critical')) {
    conditions.push('Address all critical vulnerabilities before deployment');
  }
  if (findingsArray.some((f) => f.severity === 'high')) {
    conditions.push('Implement mitigations for high-severity findings');
  }
  if (scan.scores.aivss >= 7.0) {
    conditions.push('Enable additional AI safety monitoring');
  }
  if (scan.scores.aars >= 7.0) {
    conditions.push('Implement human oversight checkpoints for autonomous actions');
  }

  if (conditions.length === 0) {
    conditions.push('Continue regular security monitoring');
  }

  return conditions;
}

/**
 * Pass through the raw targetType string; kept for future normalization.
 * We intentionally do NOT remap github-repo or other repo types — every
 * scan target is valid regardless of source.
 */
function normalizeScanTargetType(raw: string): string {
  return raw || 'code';
}

/**
 * Adapt the raw API response (ScanMetadata from KV cache or Prisma) to StoredScan format.
 * The API stores data as ScanMetadata with scores/findings nested under a `result` field,
 * while StoredScan expects them at the top level.
 */
export function adaptApiResponseToStoredScan(raw: any): StoredScan | null {
  if (!raw) return null;

  // The scan result data may be at raw.result (KV cache format) or at top level (Prisma format)
  const result = raw.result || {};
  const scores = result.scores || raw.scores || { aivss: 0, cvss: 0, aars: 0 };
  const findings = (result.findings || raw.findings || []).map((f: any) => ({
    id: f.id || '',
    title: f.title || '',
    severity: f.severity || 'info',
    description: f.description || '',
    file: f.location?.file || f.file,
    line: f.location?.line || f.line,
    fix: f.remediation || f.fix,
    category: f.category || 'general',
    source: f.source,
  }));

  return {
    scanId: raw.scanId || result.scanId || '',
    target: raw.target || '',
    targetType: normalizeScanTargetType(raw.targetType || raw.type || 'code'),
    timestamp: raw.completedAt || raw.createdAt || raw.scannedAt || new Date().toISOString(),
    scores: {
      aivss: scores.aivss || 0,
      cvss: scores.cvss || 0,
      aars: scores.aars || 0,
      ...(scores.crs != null ? { crs: scores.crs } : {}),
    },
    findings,
    fixes: (result.fixes || raw.fixes || []).map((f: any) => ({
      id: f.id || '',
      findingId: f.findingId || '',
      severity: f.severity || 'medium',
      title: f.title || '',
      description: f.description || '',
      steps: Array.isArray(f.steps) ? f.steps : [f.description || ''],
      effort: f.effort || 'medium',
      priority: f.priority ?? 1,
    })),
    metadata: {
      name: result.metadata?.name || raw.metadata?.name,
      author: raw.author || result.metadata?.author,
      filesScanned: result.metadata?.filesScanned || raw.metadata?.filesScanned,
      duration: result.metadata?.scanDuration || raw.metadata?.scanDuration,
      githubTokenPresent:
        result.metadata?.githubTokenPresent ??
        raw.metadata?.githubTokenPresent ??
        (result.vendorGuard || raw.vendorGuard)?.githubTokenPresent ??
        (result.vendorGuard || raw.vendorGuard)?.github_token_present,
    },
    recommendation: result.recommendation || raw.recommendation || 'CONDITIONAL',
    vendorGuard: result.vendorGuard || raw.vendorGuard,
    creatorTrust: result.creatorTrust || raw.creatorTrust,
    aiBom: result.aiBom || raw.aiBom,
    aivssBreakdown: result.aivssBreakdown || raw.aivssBreakdown,
    cvssBreakdown: result.cvssBreakdown || raw.cvssBreakdown,
    aarsBreakdown: result.aarsBreakdown || raw.aarsBreakdown,
    branch: raw.branch,
    // tools drives mcpTools.totalTools / highRiskTools in transformScanToInsights
    tools: result.tools || raw.tools,
    // RFIA passthrough — present when rfiaEnabled was true during scan
    rfiaFindings: result.rfiaFindings || raw.rfiaFindings,
    rfiaAggregate: result.rfiaAggregate || raw.rfiaAggregate,
    // VGE passthrough — present when files were available during scan
    vibeguardFindings: result.vibeguardFindings || raw.vibeguardFindings,
  };
}

/**
 * Fetch scan from API and transform to Insights format
 */
export interface FetchScanResult {
  report: InsightsReport | null;
  failed?: boolean;
  failureReason?: string;
  target?: string;
  scannedAt?: string;
}

export async function fetchAndTransformScan(
  apiUrl: string,
  scanId: string
): Promise<FetchScanResult | null> {
  // Build auth headers via the canonical storage reader.
  const headers: Record<string, string> = {};
  const stored = getStoredToken();
  if (stored) {
    headers['Authorization'] = `Bearer ${stored}`;
  }

  try {
    // Try V2 raw scan endpoint (has the full scan record)
    try {
      const v2Response = await fetch(`${apiUrl}/v2/scans/${scanId}`, { headers });
      if (v2Response.status === 401) {
        handleAuthFailure();
        throw new Error('AUTH_REQUIRED');
      }
      if (v2Response.ok) {
        const v2Result = await v2Response.json();
        // The API returns the raw ScanMetadata object directly from KV cache,
        // or {scan: report} from Prisma fallback. Extract the scan record.
        const raw = v2Result.scan || v2Result;

        // Detect failed scans before attempting transformation
        if (raw.status === 'failed') {
          return {
            report: null,
            failed: true,
            failureReason: raw.error || raw.recommendationReason || 'Unknown error',
            target: raw.target,
            scannedAt: raw.scannedAt || raw.updatedAt || raw.createdAt,
          };
        }

        const scanData = adaptApiResponseToStoredScan(raw);
        if (scanData) {
          if (import.meta.env.DEV) console.log('[fetchAndTransformScan] Using V2 scan data, transforming locally');
          return { report: transformScanToInsights(scanData) };
        }
      }
    } catch (e) {
      if (import.meta.env.DEV)
        console.log(
          '[fetchAndTransformScan] V2 scan endpoint not available:',
          e instanceof Error ? e.message : 'Unknown error'
        );
    }

    // Fallback: Try V2 insights endpoint
    try {
      const v2InsightsResponse = await fetch(`${apiUrl}/v2/scans/${scanId}/insights`, { headers });
      if (v2InsightsResponse.ok) {
        const v2InsightsResult = await v2InsightsResponse.json();
        const raw = v2InsightsResult.data;
        if (raw && !v2InsightsResult.error) {
          const scanData = adaptApiResponseToStoredScan(raw);
          if (scanData) {
            if (import.meta.env.DEV) console.log('[fetchAndTransformScan] Using V2 insights data, transforming locally');
            return { report: transformScanToInsights(scanData) };
          }
        }
      }
    } catch (e) {
      if (import.meta.env.DEV)
        console.log(
          '[fetchAndTransformScan] V2 insights endpoint not available:',
          e instanceof Error ? e.message : 'Unknown error'
        );
    }

    console.error('Failed to fetch scan:', scanId);
    return null;
  } catch (error) {
    console.error('Error fetching scan:', error instanceof Error ? error.message : 'Unknown error');
    return null;
  }
}

// =============================================================================
// HEATMAP HELPERS (shared with sub-report pages)
// =============================================================================

export interface HeatmapPoint {
  label: string;
  likelihood: 'low' | 'moderate' | 'moderately-high' | 'high';
  impact: 'low' | 'moderate' | 'moderately-high' | 'high';
  description: string;
  recommendation: string;
  linkedSection: string;
}

/** Build a heatmap array from score-breakdown metrics */
export function buildHeatmapFromMetrics(metrics: ScoreBreakdownItem[]): HeatmapPoint[] {
  if (!metrics || metrics.length === 0) return [];
  const getLikelihood = (score: number): HeatmapPoint['likelihood'] => {
    if (score >= 8) return 'high';
    if (score >= 6) return 'moderately-high';
    if (score >= 4) return 'moderate';
    return 'low';
  };
  const getImpact = (weight: number): HeatmapPoint['impact'] => {
    if (weight >= 25) return 'high';
    if (weight >= 20) return 'moderately-high';
    if (weight >= 15) return 'moderate';
    return 'low';
  };
  // Skip metrics that were never measured — matches ReportPage.generateHeatmapFromScores behaviour
  return metrics
    .filter((m) => typeof m.value === 'number' && m.value > 0)
    .map((m) => ({
      label: m.name,
      likelihood: getLikelihood(m.value),
      impact: getImpact(m.weight),
      description: m.description || `Risk factor: ${m.name}`,
      recommendation: `Address ${m.name} to improve security.`,
      linkedSection: 'scoring-breakdown',
    }));
}

/** Shape of the score breakdowns object used in report views */
export interface ScoreBreakdowns {
  aivss: ScoreBreakdownItem[];
  cvss: ScoreBreakdownItem[];
  aars: ScoreBreakdownItem[];
}

/** Shape returned by buildHeatmapFromScores — used as HeatmapShape in report pages */
export interface HeatmapFromScoresResult {
  aivss: HeatmapPoint[];
  cvss: HeatmapPoint[];
  aars: HeatmapPoint[];
  vulnerability: HeatmapPoint[];
}

/** Build a heatmap array from individual VulnerabilityDetail items */
export function buildHeatmapFromVulnerabilities(vulns: VulnerabilityDetail[]): HeatmapPoint[] {
  if (!vulns || vulns.length === 0) return [];

  const severityToLevel: Record<string, HeatmapPoint['likelihood']> = {
    critical: 'high',
    high: 'moderately-high',
    medium: 'moderate',
    low: 'low',
    info: 'low',
  };

  return vulns.map((v) => {
    const sev = (v.severity || 'low').toLowerCase();
    const rawLabel = v.title || v.id || 'Issue';
    const label = rawLabel.length > 20 ? rawLabel.slice(0, 20) + '…' : rawLabel;
    return {
      label,
      likelihood: severityToLevel[sev] ?? 'low',
      impact: severityToLevel[sev] ?? 'low',
      description: v.description || rawLabel,
      recommendation: v.remediation || `Remediate: ${rawLabel}`,
      linkedSection: 'vulnerability-assessment',
    };
  });
}

/**
 * Build heatmap data from score breakdowns + optional vulnerability details.
 * Replaces the duplicated `generateHeatmapFromScores` previously inlined in
 * ReportPage.tsx and PrintableReportPage.tsx.
 */
export function buildHeatmapFromScores(
  scoreBreakdowns: ScoreBreakdowns | null | undefined,
  vulnerabilityDetails?: VulnerabilityDetail[],
): HeatmapFromScoresResult {
  if (!scoreBreakdowns) {
    return { aivss: [], cvss: [], aars: [], vulnerability: [] };
  }
  return {
    aivss: buildHeatmapFromMetrics(scoreBreakdowns.aivss),
    cvss: buildHeatmapFromMetrics(scoreBreakdowns.cvss),
    aars: buildHeatmapFromMetrics(scoreBreakdowns.aars),
    vulnerability: buildHeatmapFromVulnerabilities(vulnerabilityDetails || []),
  };
}

const VULN_CAT_LABELS: Record<string, string> = {
  injection: 'Input Sanitization',
  INJECTION: 'Input Sanitization',
  secrets: 'Secrets Management',
  SECRETS: 'Secrets Management',
  code: 'Code Security',
  CODE: 'Code Security',
  ai: 'AI Safety',
  AI: 'AI Safety',
};

const SEV_TO_LIKELIHOOD: Record<string, HeatmapPoint['likelihood']> = {
  critical: 'high',
  high: 'moderately-high',
  medium: 'moderate',
  low: 'low',
  info: 'low',
};

/** Build a heatmap array from vulnerability findings grouped by category */
export function buildVulnHeatmap(vulns: Finding[]): HeatmapPoint[] {
  if (!vulns || vulns.length === 0) return [];
  const categories: Record<string, { count: number; maxSeverity: string }> = {};
  const sevOrder = ['info', 'low', 'medium', 'high', 'critical'];
  vulns.forEach((v) => {
    const cat = v.category || v.id?.split('-')[0] || 'OTHER';
    if (!categories[cat]) categories[cat] = { count: 0, maxSeverity: 'low' };
    categories[cat].count++;
    if (sevOrder.indexOf(v.severity) > sevOrder.indexOf(categories[cat].maxSeverity)) {
      categories[cat].maxSeverity = v.severity;
    }
  });
  return Object.entries(categories).map(([cat, data]) => ({
    label: VULN_CAT_LABELS[cat] || cat,
    likelihood: SEV_TO_LIKELIHOOD[data.maxSeverity] || 'moderate',
    impact: (data.count >= 10
      ? 'high'
      : data.count >= 5
        ? 'moderately-high'
        : data.count >= 2
          ? 'moderate'
          : 'low') as HeatmapPoint['impact'],
    description: `${data.count} ${data.maxSeverity} severity issues`,
    recommendation: `Review ${cat.toLowerCase()} vulnerabilities`,
    linkedSection: 'vulnerability-assessment',
  }));
}

// =============================================================================
// UNIFIED TRANSFORM — used by PrintableReportPage & BusinessSummaryPage
// =============================================================================

export interface TransformedReportData extends InsightsReport {
  /** Alias for prioritizedRemediation — kept for component compatibility */
  remediationItems: RemediationItem[];
  heatmapData: {
    aivss: HeatmapPoint[];
    cvss: HeatmapPoint[];
    aars: HeatmapPoint[];
    vulnerability: HeatmapPoint[];
  };
  /** Raw scan ID (same as reportId, preserved for convenience) */
  scanId?: string;
  /** Direct passthrough of API mcpTools when richer than derived value */
  mcpTools: InsightsReport['mcpTools'];
}

/**
 * Transform raw API scan data — any of the formats that may arrive at sub-report pages
 * (window-injected, token-fetched, scan_id-fetched) — into the unified shape
 * all report components expect.
 *
 * Returns `any` so that components accessing both InsightsReport fields AND raw API
 * passthrough fields (metadata, findings, target, etc.) do not get TS errors.
 * The underlying data is always properly structured per `TransformedReportData`.
 *
 * Replaces the duplicated local `transformApiData` functions previously inside
 * PrintableReportPage and BusinessSummaryPage, which had drifted out of sync
 * and produced different values for the same underlying scan.
 *
 * @param rawData - Raw data from any source (API response, window global, base64 param)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function transformRawApiData(rawData: any): any {
  if (!rawData) return null;

  // Already an InsightsReport — just add the alias fields and return
  if (
    rawData.vulnerabilities?.critical !== undefined &&
    rawData.scores?.aivss?.level !== undefined
  ) {
    const breakdowns = rawData.scoreBreakdowns || { aivss: [], cvss: [], aars: [] };
    const findings = rawData.vulnerabilityDetails || rawData.findings || [];
    return {
      ...rawData,
      remediationItems: rawData.remediationItems || rawData.prioritizedRemediation || [],
      heatmapData: rawData.heatmapData || {
        aivss: buildHeatmapFromMetrics(breakdowns.aivss),
        cvss: buildHeatmapFromMetrics(breakdowns.cvss),
        aars: buildHeatmapFromMetrics(breakdowns.aars),
        vulnerability: buildVulnHeatmap(findings),
      },
    };
  }

  // Adapt the raw response into StoredScan format, then transform to InsightsReport
  const storedScan = adaptApiResponseToStoredScan(rawData);
  if (!storedScan) return null;

  const report = transformScanToInsights(storedScan);
  const breakdowns = report.scoreBreakdowns;

  // API-provided rich fields take precedence over derived values
  const resultData = rawData.result || {};
  const mcpToolsRaw = resultData.mcpTools || rawData.mcpTools;
  const permissionsRaw = resultData.permissions || rawData.permissions;
  const threatModelingRaw = resultData.threatModeling || rawData.threatModeling;

  return {
    // Preserve ALL raw API fields so components can access metadata, findings,
    // target, targetType, etc. without TS errors (matches old {…rawData, …transformed} pattern)
    ...rawData,
    // Overlay with properly computed InsightsReport fields
    ...report,
    // Prefer directly-provided API data over derived values when available
    mcpTools: mcpToolsRaw || report.mcpTools,
    permissions: permissionsRaw || report.permissions,
    threatModeling: threatModelingRaw || report.threatModeling,
    // RFIA passthrough — prefer raw API data over derived values
    rfiaFindings: resultData.rfiaFindings || rawData.rfiaFindings || report.rfiaFindings,
    rfiaAggregate: resultData.rfiaAggregate || rawData.rfiaAggregate || report.rfiaAggregate,
    // VGE passthrough
    vibeguardFindings: resultData.vibeguardFindings || rawData.vibeguardFindings || report.vibeguardFindings,
    // Component aliases
    remediationItems: report.prioritizedRemediation,
    heatmapData: {
      aivss: buildHeatmapFromMetrics(breakdowns.aivss),
      cvss: buildHeatmapFromMetrics(breakdowns.cvss),
      aars: buildHeatmapFromMetrics(breakdowns.aars),
      vulnerability: buildVulnHeatmap(storedScan.findings),
    },
  };
}

export default transformScanToInsights;
